import * as React from "react";
import { AlertCircle, CalendarPlus, Loader2 } from "lucide-react";
import {
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Textarea,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import { ApiError } from "@eduviaos/shared";
import { useTerms } from "@/hooks/useTerms";
import { useGeneratePayRun, type PayRunDetail } from "@/lib/payroll/api";

/**
 * Phase SC-2c — "Generate pay run" drawer.
 *
 * Picks a month + year and seeds a draft run from the active staff-cost
 * ledger (one payslip per active StaffPayee, gross = their monthly amount).
 * The BE rejects a duplicate run for the same period, surfaced inline.
 */

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function recentYears(): number[] {
  const y = new Date().getFullYear();
  return [y + 1, y, y - 1, y - 2];
}

export interface GeneratePayRunSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGenerated?: (detail: PayRunDetail) => void;
}

export function GeneratePayRunSheet({ open, onOpenChange, onGenerated }: GeneratePayRunSheetProps) {
  const { ct } = useTerms();
  const now = new Date();
  const [year, setYear] = React.useState(now.getFullYear());
  const [month, setMonth] = React.useState(now.getMonth() + 1);
  const [note, setNote] = React.useState("");
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setYear(now.getFullYear());
      setMonth(now.getMonth() + 1);
      setNote("");
      setSubmitError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const generateMutation = useGeneratePayRun();
  const isSubmitting = generateMutation.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    try {
      const detail = await generateMutation.mutateAsync({
        year,
        month,
        note: note.trim() || null,
      });
      toast.success(
        `Drafted ${MONTHS[month - 1]} ${year} pay run with ${detail.payslips.length} payslip${
          detail.payslips.length === 1 ? "" : "s"
        }.`,
      );
      onGenerated?.(detail);
      onOpenChange(false);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message || "Couldn't generate the pay run. Please retry."
          : err instanceof Error
            ? err.message
            : "Couldn't generate the pay run. Please retry.";
      setSubmitError(message);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[440px]"
        data-testid="generate-pay-run-sheet"
      >
        <SheetHeader className="border-border/60 border-b px-6 pb-4 pt-6">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="bg-primary/10 text-primary mt-0.5 grid size-10 shrink-0 place-items-center rounded-lg"
            >
              <CalendarPlus className="size-5" />
            </span>
            <div className="flex-1">
              <SheetTitle className="tracking-tight">Generate pay run</SheetTitle>
              <SheetDescription className="mt-1 text-xs">
                {ct(
                  "Draft a month's payroll from your active staff costs. You can edit every payslip before finalizing.",
                )}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
            {submitError ? (
              <div
                role="alert"
                className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
              >
                <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
                <span className="flex-1">{submitError}</span>
              </div>
            ) : null}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="pr-month" className="text-xs">
                  Month
                </Label>
                <div className="mt-1">
                  <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
                    <SelectTrigger id="pr-month">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTHS.map((m, i) => (
                        <SelectItem key={m} value={String(i + 1)}>
                          {m}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="pr-year" className="text-xs">
                  Year
                </Label>
                <div className="mt-1">
                  <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                    <SelectTrigger id="pr-year">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {recentYears().map((y) => (
                        <SelectItem key={y} value={String(y)}>
                          {y}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="pr-note" className="text-xs">
                Note
              </Label>
              <div className="mt-1">
                <Textarea
                  id="pr-note"
                  value={note}
                  maxLength={2000}
                  rows={2}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Optional — e.g. includes Diwali bonus."
                />
              </div>
            </div>

            <p className="text-muted-foreground text-xs">
              {ct(
                "Everyone active in your staff costs gets a draft payslip. Add other people or adjust amounts before you finalize.",
              )}
            </p>
          </div>

          <div className="border-border/60 bg-card/40 flex items-center justify-end gap-2 border-t px-6 py-4 backdrop-blur">
            <SheetClose asChild>
              <Button variant="ghost" type="button" disabled={isSubmitting}>
                Cancel
              </Button>
            </SheetClose>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <Loader2 aria-hidden="true" className={cn("size-4 animate-spin")} />
              ) : null}
              Generate draft
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
